import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import EMPBanner from "../../shared/components/EMPBanner/EMPBanner";
import EMPButton from "../../shared/components/EMPButton/EMPButton";
import MotionChevronRightCircle from "../../shared/Icons/MotionChevronRightCircle";
import { IS_FORD_BRAND } from "../../utils/EmpUtil";
import useRoleUsers from "../../shared/hooks/useRoleUsers";
import styles from "./CanadianPreOrderEnrollmentPage.module.scss";

export const ACCESS_MANAGEMENT_URL = "https://www.accessmgmt.ford.com/aga-home";
export const CUSTOMER_HANDLING_ROLE = "EMP_eComm_CustomerHandling";

const getRoleUsers = (roleUsers) => {
  const matchingRole = Object.keys(roleUsers).find(
    (role) => role.toUpperCase() === CUSTOMER_HANDLING_ROLE.toUpperCase()
  );

  return matchingRole ? roleUsers[matchingRole] || [] : [];
};

const getUserLabel = (user) =>
  user?.name ||
  user?.fullName ||
  user?.userId ||
  user?.cdsid ||
  "Dealership user";

const CustomerHandlingEntitlement = ({
  user,
  onStatusChange,
  isExpanded = true,
}) => {
  const { t } = useTranslation();
  const dealerCommonId = user?.commonId || user?.commonDealerId;
  const {
    data: roleUsers = {},
    isLoading,
    isError,
    refetch,
  } = useRoleUsers(dealerCommonId, { enabled: isExpanded });
  const users = getRoleUsers(roleUsers);
  const isFordBrand = IS_FORD_BRAND(user?.brand);

  useEffect(() => {
    onStatusChange(users.length > 0);
  }, [onStatusChange, users.length]);

  useEffect(() => {
    if (!isExpanded) return undefined;

    const refreshRoleUsers = () => refetch();
    window.addEventListener("focus", refreshRoleUsers);

    return () => window.removeEventListener("focus", refreshRoleUsers);
  }, [refetch, isExpanded]);

  return (
    <Box
      className={styles.entitlementContent}
      data-testid="customer-handling-entitlement"
    >
      <Typography component="p" className={styles.bodyCopy}>
        {t("CanadianPreOrderEnrollment.entitlements.description1", {
          defaultValue:
            "The customer handling entitlement allows authorized users to take action on customer requests to pre-order the Ford Fathom, and these requests flow into the dealership CRM and can be assessed in EMP or RouteOne (if a financing application was submitted) for additional detail.",
        })}
      </Typography>
      <Typography component="p" className={styles.bodyCopy}>
        {t("CanadianPreOrderEnrollment.entitlements.description2", {
          defaultValue:
            "The authorized user will receive email alerts and have the ability to: Accept or reject orders and access customer information in EMP. Only users authorized to manage customer submissions should be granted this entitlement. Be sure to assign this entitlement to yourself and any other relevant dealership employees.",
        })}
      </Typography>
      <Typography component="p" className={styles.entitlementLabel}>
        {t("CanadianPreOrderEnrollment.entitlements.entitlementLabel", {
          defaultValue: "Entitlements to be set:",
        })}{" "}
        <strong>{CUSTOMER_HANDLING_ROLE}</strong>
      </Typography>
      <Typography component="p" className={styles.usersLabel}>
        {t("CanadianPreOrderEnrollment.entitlements.usersLabel", {
          defaultValue: "Dealership users with this entitlement:",
        })}
      </Typography>
      {isLoading && (
        <Box
          className={styles.loadingState}
          data-testid="customer-handling-loading"
          role="status"
        >
          <CircularProgress size={20} />
          <Typography component="span">
            {t("CanadianPreOrderEnrollment.entitlements.loading", {
              defaultValue: "Loading dealership users...",
            })}
          </Typography>
        </Box>
      )}
      {!isLoading && isError && (
        <Box
          className={styles.errorState}
          data-testid="customer-handling-error"
        >
          <EMPBanner
            severity="error"
            title={t("CanadianPreOrderEnrollment.entitlements.loadError", {
              defaultValue: "Please try again",
            })}
            content=""
          />
          <EMPButton
            variant="outlined"
            isFord={isFordBrand}
            onClick={() => refetch()}
          >
            {t("CanadianPreOrderEnrollment.entitlements.retry", {
              defaultValue: "Retry",
            })}
          </EMPButton>
        </Box>
      )}
      {!isLoading && !isError && users.length === 0 && (
        <EMPBanner
          severity="info"
          title={t("CanadianPreOrderEnrollment.entitlements.noUsers", {
            defaultValue: "No users added.",
          })}
          content=""
        />
      )}
      {!isLoading && !isError && users.length > 0 && (
        <Box
          className={styles.userGrid}
          data-testid="entitlement-user-list"
          role="list"
        >
          {users.map((roleUser, index) => (
            <Box
              className={styles.userCard}
              key={roleUser?.userId || roleUser?.cdsid || index}
              role="listitem"
            >
              <Typography component="p" className={styles.userName}>
                {getUserLabel(roleUser)}
              </Typography>
              {(roleUser?.role || roleUser?.phone) && (
                <Typography component="p" className={styles.userDetails}>
                  {[roleUser.role, roleUser.phone].filter(Boolean).join(", ")}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      )}
      {!isLoading && !isError && (
        <EMPButton
          variant="contained"
          href={ACCESS_MANAGEMENT_URL}
          target="_blank"
          rel="noopener noreferrer"
          isFord={isFordBrand}
          rightIcon={<MotionChevronRightCircle />}
          sx={{ alignSelf: "flex-start", minWidth: "auto", width: "auto" }}
        >
          {t("CanadianPreOrderEnrollment.entitlements.edit", {
            defaultValue: "Edit Entitlements",
          })}
        </EMPButton>
      )}
    </Box>
  );
};

CustomerHandlingEntitlement.propTypes = {
  user: PropTypes.shape({
    brand: PropTypes.string,
    commonDealerId: PropTypes.string,
    commonId: PropTypes.string,
  }),
  onStatusChange: PropTypes.func.isRequired,
  isExpanded: PropTypes.bool,
};

export default CustomerHandlingEntitlement;
